import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface MovieCardProps {
  movie: Movie;
  variant?: 'poster' | 'landscape';
}

export default function MovieCard({ movie, variant = 'landscape' }: MovieCardProps) {
  if (variant === 'poster') {
    return (
      <Link
        to={`/article/${movie.id}`}
        className="group block cursor-pointer"
      >
        <div className="relative overflow-hidden rounded-lg aspect-[2/3]">
          <img
            src={movie.poster}
            alt={movie.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
            title={`${movie.title} - ${movie.subtitle}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="absolute top-2 left-2">
            <span className="inline-block px-2 py-0.5 bg-primary-500 text-white text-xs rounded-md whitespace-nowrap">
              {movie.rating}
            </span>
          </div>
        </div>
        <h3 className="mt-2 text-sm font-heading font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">{movie.subcategory} · {movie.year}</p>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${movie.id}`}
      className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 cursor-pointer transition-colors"
    >
      <div className="relative w-40 sm:w-48 flex-shrink-0 overflow-hidden rounded-lg">
        <img
          src={movie.image}
          alt={movie.title}
          className="w-full aspect-video object-cover object-top transition-transform duration-500 group-hover:scale-110"
          title={`${movie.title} - ${movie.subtitle}`}
        />
        <div className="absolute top-2 left-2">
          <span className="inline-block px-1.5 py-0.5 bg-primary-500 text-white text-[10px] rounded whitespace-nowrap">
            {movie.rating}
          </span>
        </div>
        {movie.isNew && (
          <div className="absolute top-2 right-2">
            <span className="inline-block px-1.5 py-0.5 bg-accent-500 text-white text-[10px] rounded whitespace-nowrap">
              新
            </span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 py-1">
        <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
          {movie.description}
        </p>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className="text-xs text-foreground-400">{movie.subcategory}</span>
          <span className="text-xs text-foreground-400">·</span>
          <span className="text-xs text-foreground-400">{movie.year}</span>
          <span className="text-xs text-foreground-400">·</span>
          <span className="text-xs text-foreground-400">{movie.duration}</span>
        </div>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {movie.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="inline-block px-2 py-0.5 bg-secondary-100 text-secondary-700 text-[10px] rounded-full whitespace-nowrap"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}